import Image from "next/image";
import Link from "next/link";
import type { ReactNode } from "react";
import { useGetString } from "../../hooks/useGetString";
import { useReservationPhase } from "../../hooks/useReservationPhase";
import { useVenueTimezone } from "../../hooks/venue";
import getStaticVenuePaths from "../../lib/booking/getStaticVenuePaths";
import { states } from "../../lib/booking/reservation-states";
import { getTicketDate } from "../../lib/dayjs";
import { useLabels } from "../../lib/translations/useLabels";
import { formatDisplayTimeRange } from "../../lib/utils/formatDisplayTimeRange";
import { businessDate } from "../../lib/utils/seatingTime";
import PinchosLogo from "../../public/assets/images/pinchos-circle-logo.png";
import { formatVenueAddress, useGetVenueByIdQuery } from "../../services/s3Api";
import { reservationPhaseLabelPath } from "../ReservationPhaseNotice";
import BookingSummary from "../_Atoms/BookingSummary";
import Icon from "../_Atoms/Icon";
import { String } from "../string";

/** The SVG files are 343×206; only the header + perforation (94 tall) is shown. */
const HEADER = { width: 343, height: 94, goldLine: 77.5 };

const ARTWORK = {
	red: "/assets/images/redTicket.svg",
	redLabel: "/assets/images/redTicketLabel.svg",
	whiteLabel: "/assets/images/whiteTicketLabel.svg",
} as const;

/**
 * `phaseLabel` is set once the booking is locked (ongoing, past, expired, ...). It takes the
 * band's place, so a locked red ticket gets the labelled artwork even when it is indoors.
 */
function ticketLook(reservation: Reservation, phaseLabel?: string) {
	const waitlistOrRequest =
		reservation?.state === states.WAITLIST ||
		reservation?.state === states.REQUESTED;

	if (waitlistOrRequest) {
		return {
			src: ARTWORK.whiteLabel,
			body: "bg-white",
			text: "text-redThree",
			band: "red" as const,
			labelId: `state.${states[reservation.state].toLowerCase()}`,
		};
	}
	if (phaseLabel) {
		return {
			src: ARTWORK.redLabel,
			body: "bg-redSix",
			text: "text-white",
			band: "gold" as const,
			labelId: null,
		};
	}
	if (reservation?.zone === "outdoors") {
		return {
			src: ARTWORK.redLabel,
			body: "bg-redSix",
			text: "text-white",
			band: "gold" as const,
			labelId: `zone.${reservation.zone}`,
		};
	}
	return {
		src: ARTWORK.red,
		body: "bg-redSix",
		text: "text-white",
		band: null,
		labelId: null,
	};
}

function Band({
	children,
	tone,
	phrase = false,
}: {
	children: ReactNode;
	tone: "gold" | "red";
	/** A full phrase ("Tidigare bokning"): kept on one line and in its own casing. */
	phrase?: boolean;
}) {
	const line = tone === "gold" ? "bg-darkGold" : "bg-redThree";
	const text = tone === "gold" ? "text-darkGold" : "text-redThree";
	return (
		<div className={`flex w-full items-center font-bold ${text}`}>
			<span className={`h-px flex-1 ${line}`} aria-hidden="true" />
			<span className={`mx-2 ${phrase ? "whitespace-nowrap" : "capitalize"}`}>
				{children}
			</span>
			<span className={`h-px flex-1 ${line}`} aria-hidden="true" />
		</div>
	);
}

interface Props {
	bookingId: string;
	peopleHeader: ReactNode;
	people: ReactNode;
	venueId: string;
	locale: string;
	link?: boolean;
	reservation: Reservation;
	editable?: boolean;
	className?: string;
	/** Turns the address into the "view on map" control. */
	onMapClick?: () => void;
	/** Turns the date into the "add to calendar" control. */
	onCalendarClick?: () => void;
}

export default function Ticket({
	bookingId,
	peopleHeader,
	people,
	reservation,
	locale,
	link = false,
	className,
	onMapClick,
	onCalendarClick,
}: Props) {
	const { getLabel } = useLabels();
	const getString = useGetString();
	const { phase, isLocked } = useReservationPhase(reservation);
	// BookingSummary already marks cancelled bookings, so a band would only repeat it.
	const phaseLabel =
		isLocked && phase !== "cancelled"
			? getLabel(reservationPhaseLabelPath(phase))
			: undefined;
	const look = ticketLook(reservation, phaseLabel);
	const cancelled = reservation?.state === states.CANCELLED;
	// Ongoing visits keep full colour; anything that is over is dimmed like a cancelled booking.
	const faded =
		isLocked && phase !== "started" && phase !== "seated" ? "opacity-50" : "";

	const venueTimezone = useVenueTimezone(reservation?.venueId);
	const isWaitlist = reservation?.state === states.WAITLIST;
	const waitlistRange =
		isWaitlist && reservation?.waitlistQuartiles?.length > 0
			? reservation.waitlistQuartiles
			: null;

	const formattedTime = formatDisplayTimeRange(
		waitlistRange ? waitlistRange[0] : reservation?.startQuartile,
		waitlistRange
			? waitlistRange[waitlistRange.length - 1]
			: reservation?.endQuartile,
		locale,
		venueTimezone,
	);
	// The restaurant's business day, as booked. Not seatingDate's calendar day,
	// which depends on the browser's timezone and, once booking-api corrects
	// seatingDate, is the next day for a seating after midnight.
	const formattedDate = getTicketDate(businessDate(reservation?.dateString), locale);

	const venueData = useGetVenueByIdQuery({
		lang: locale,
		venueId: reservation?.venueId,
		slim: true,
	});
	const venue = venueData?.data?.title;
	const address = formatVenueAddress(venueData?.data);
	const summaryLabel = `${getLabel("booking.view")} ${venue} ${address}. ${getLabel("quickEditTabs.seatings")} ${reservation?.seatings}. ${getLabel("quickEditTabs.time")} ${formattedTime}. ${getLabel("quickEditTabs.date")} ${formattedDate}.${phaseLabel ? ` ${phaseLabel}.` : ""}`;

	return (
		<div className={`relative mx-auto w-full max-w-[460px] ${className ?? ""}`}>
			{/* Covers the card rather than wrapping it, so the address and date can carry
          their own controls without nesting buttons inside a link. */}
			{link && (
				<Link
					href={{ pathname: `/reservation/${bookingId}` }}
					aria-label={summaryLabel}
					className="absolute inset-0 z-10 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-darkGold focus-visible:ring-offset-2"
				/>
			)}
			<header className={`relative overflow-hidden ${look.text} ${faded}`}>
				{/* Crop the 343×206 artwork to the header + perforation. */}
				<Image
					src={look.src}
					alt=""
					width={343}
					height={206}
					unoptimized
					className="block w-full object-cover object-top"
					style={{ aspectRatio: `${HEADER.width} / ${HEADER.height}` }}
				/>
				<div className="absolute inset-0 flex flex-col px-8">
					<div
						className="flex items-center gap-2"
						style={{ flex: `${HEADER.goldLine} 1 0%` }}
					>
						<Image
							src={PinchosLogo}
							width={32}
							height={32}
							alt="Pinchos logo"
							className="h-8 w-8 shrink-0"
						/>
						<div className="text-[1.063rem] font-[550] capitalize leading-5 tracking-[0.85px]">
							{venue}
							<br />
							<span className="flex flex-wrap items-center gap-x-2 text-sm font-normal normal-case tracking-normal">
								{address && (
									<span className="capitalize opacity-50">{address}</span>
								)}
								{onMapClick && address && (
									<button
										type="button"
										data-testid="ticket-view-on-map"
										onClick={onMapClick}
										className="relative z-20 inline-flex items-center gap-1 text-sm font-normal normal-case tracking-normal text-darkGold underline decoration-dotted underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-darkGold"
									>
										<Icon
											type="map"
											className="h-3 w-3 shrink-0"
											aria-hidden="true"
										/>
										{getString("labels.view-on-map")}
									</button>
								)}
							</span>
						</div>
					</div>
					<div
						className="flex items-center"
						style={{ flex: `${HEADER.height - HEADER.goldLine} 1 0%` }}
					>
						{phaseLabel && look.band ? (
							<Band tone={look.band} phrase>
								<span data-testid="ticket-phase-label">{phaseLabel}</span>
							</Band>
						) : (
							look.labelId &&
							look.band && (
								<Band tone={look.band}>
									<String id={look.labelId} />
								</Band>
							)
						)}
					</div>
				</div>
			</header>
			<div
				className={`rounded-b-sm pt-1 pb-8 ${look.text} ${look.body} ${faded}`}
			>
				<BookingSummary
					seatings={reservation?.seatings}
					peopleHeader={peopleHeader}
					timeHeader={
						waitlistRange && waitlistRange.length > 1
							? getString("form.steps.wait.time.button")
							: getString("labels.time")
					}
					people={people}
					time={formattedTime}
					date={formattedDate}
					dateHeader={getString("labels.date")}
					isCancelled={cancelled}
					onCalendarClick={
						onCalendarClick && !isLocked ? onCalendarClick : undefined
					}
					calendarLabel={`${getString("labels.add-to-calendar")}: ${formattedDate}`}
					edgeToEdge
				/>
			</div>
		</div>
	);
}

export const getStaticPaths = getStaticVenuePaths();
